import { Component } from "@theme/component";
import { debounce, onDocumentLoaded } from "@theme/utilities";
import { MegaMenuHoverEvent } from "@theme/events";

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ["overflowMenu"];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Close submenu when pointer leaves the overflow menu
    this.overflowMenu?.addEventListener(
      "pointerleave",
      () => this.#debouncedDeactivate(),
      { signal: this.#abortController.signal }
    );

    // Detect clicks/taps outside the header to close
    ["pointerdown", "touchstart"].forEach((ev) => {
      document.addEventListener(ev, this.#handleOutsideClick, {
        signal: this.#abortController.signal,
      });
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(":hover") ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute("data-animating");

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);
    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === "";
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = "false";
      const prevTrigger =
        previouslyActiveItem.querySelector('[ref="menuitem"]');
      if (prevTrigger) {
        prevTrigger.setAttribute("aria-expanded", "false");
        // Only remove class if it's NOT the current page
        if (!prevTrigger.hasAttribute('data-page-active')) {
          prevTrigger?.classList.remove("menu-list__link--active");
        }
      }
    }

    this.#state.activeItem = item;
    this.ariaExpanded = "true";
    item.ariaExpanded = "true";

    // Ensure the <a ref="menuitem"> (if present) mirrors the state
    const trigger = item.querySelector('[ref="menuitem"]');
    if (trigger) {
      trigger.setAttribute("aria-expanded", "true");
      trigger?.classList.add("menu-list__link--active");
    }

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu
      ? Math.max(submenu.offsetHeight, overflowMenuHeight)
      : 0;

    this.style.setProperty("--submenu-height", `${submenuHeight}px`);
    this.style.setProperty("--submenu-opacity", "1");
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if ("pointerType" in event && event.pointerType === "touch") return;

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }
  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty("--submenu-height", "0px");
    this.style.setProperty("--submenu-opacity", "0");
    this.dataset.overflowExpanded = "false";

    this.#state.activeItem = null;
    this.ariaExpanded = "false";
    item.ariaExpanded = "false";

    // Reset also on <a ref="menuitem">
    const trigger = item.querySelector('[ref="menuitem"]');
    if (trigger) {
      trigger.setAttribute("aria-expanded", "false");
      // Only remove class if it's NOT the current page
      if (!trigger.hasAttribute('data-page-active')) {
        trigger?.classList.remove("menu-list__link--active");
      }
    }

    item.setAttribute("data-animating", "");
    setTimeout(() => {
      item.removeAttribute("data-animating");
    }, Math.max(0, this.animationDelay - 150));
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Close menu when a tap/click happens outside the header
   * @param {Event} event
   */
  #handleOutsideClick = (event) => {
    const header = this.closest("header, .header, .site-header");
    if (header && !header.contains(event?.target)) {
      this.#deactivate();
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute("loading"));
  };
}

if (!customElements.get("header-menu")) {
  customElements.define("header-menu", HeaderMenu);
}

/**
 * Find the menu item element associated with a given element.
 * - Uses `closest` first (prefers ancestor with ref/data attributes / li)
 * - Falls back to searching for a descendant [ref="menuitem"] if needed.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  // Special case: "more" slot
  if (element.matches('[slot="more"]')) {
    return findMenuItem(
      element.parentElement?.querySelector('[slot="overflow"]')
    );
  }

  // Try to find the closest menu item container/trigger (ancestor)
  const closestItem =
    element.closest('[ref="menuitem"]') ||
    element.closest("[data-menuitem]") ||
    element.closest('[role="menuitem"]') ||
    element.closest("li");

  if (closestItem instanceof HTMLElement) return closestItem;

  // Fallback: maybe the element itself contains a child with ref="menuitem"
  const descendantTrigger = element.querySelector('[ref="menuitem"]');
  return descendantTrigger instanceof HTMLElement ? descendantTrigger : null;
}

/**
 * Find the submenu associated with an item (sibling/descendant).
 * @param {Element | null | undefined} item
 * @returns {HTMLElement | null}
 */
function findSubmenu(item) {
  if (!(item instanceof Element)) return null;

  const root = item.closest("li, [data-menuitem-root]") || item;

  let submenu = root.querySelector(
    '[ref="submenu[]"], [data-submenu], .submenu, [role="menu"]'
  );

  // Fallback by aria-controls
  if (!submenu && item instanceof HTMLElement) {
    const controls = item.getAttribute("aria-controls");
    if (controls) {
      const byId = document.getElementById(controls);
      if (byId instanceof HTMLElement) submenu = byId;
    }
  }

  return submenu instanceof HTMLElement ? submenu : null;
}
